// Classi e helper condivisi dalle cronologie (lead e offerta): card flat stile Notion con
// kicker gradiente Flusia, cluster azioni flottante e date secondarie compatte.
// Riferimento: MD/features/2026-08-10-drawer-note-ui-ux-redesign-proposta.md (sezione
// "Cronologia - card") e mockup vista 5.

/** Card della cronologia: flat, niente ombra, niente bordo sinistro colorato. */
export const CARD_CLS =
  'group relative bg-white dark:bg-[#2F3437] border border-[rgba(55,53,47,0.16)] dark:border-[#3F4447] ' +
  'rounded-lg p-3 mb-3 transition-colors hover:border-[rgba(55,53,47,0.26)] dark:hover:border-[#565D61]';

/**
 * Card di una Task commerciale nella cronologia del Lead (segnalazione e83a59a3): stessa
 * card, fondo caldo appena accennato (tono "attivita'" degli accenti) per distinguerla
 * dalle note, che restano bianche.
 */
export const ACTIVITY_CARD_CLS =
  'group relative bg-[#FBF8F1] dark:bg-[#33302A] border border-[rgba(154,106,10,0.18)] dark:border-[#4A4336] ' +
  'rounded-lg p-3 mb-3 transition-colors hover:border-[rgba(154,106,10,0.32)] dark:hover:border-[#5E5545]';

/**
 * Riga di testa a chip, oggi usata solo dal riassunto email (ClientEmailSummaryCard):
 * le card della cronologia non hanno piu' una testa, aprono col contenuto e chiudono
 * con `TimelineFooter`.
 */
export const CARD_HEAD_CLS = 'flex flex-wrap items-center gap-2';

/**
 * Icona 22px col tratto rosa Flusia, senza sfondo.
 *
 * La pastiglia tinta c'era su ogni card della cronologia e in colonna faceva
 * l'effetto tessere colorate: tolta ovunque, resta il solo tratto. Il gradiente
 * Flusia sopravvive nel kicker delle card di sistema, dove ha un significato.
 */
export const ICON_CHIP_CLS =
  'flex-shrink-0 h-[22px] w-[22px] flex items-center justify-center text-[#E91E8C]';

/**
 * Accento di colore della card, per famiglia di contenuto.
 *
 * Perche' per famiglia e non per canale: le card della cronologia avevano tutte
 * la stessa iconcina rosa e lo stesso kicker col gradiente Flusia, quindi a colpo
 * d'occhio erano indistinguibili e l'unico segnale era un glifo da 12 pixel.
 * Cinque famiglie si imparano, undici colori diversi no: il colore dice di che
 * genere di cosa si tratta, l'icona e l'etichetta dicono il canale preciso.
 *
 *  - `nota`          quello che scriviamo noi
 *  - `attivita`      quello che c'e' da fare (task, chiamate, riunioni)
 *  - `conversazione` messaggi scambiati (WhatsApp, Instagram, Messenger)
 *  - `email`         posta e newsletter
 *  - `marketing`     da dove arriva il contatto (annunci)
 *  - `flusia`        quando parla il sistema (suggerimenti IA, automazioni)
 */
export type CardAccent = 'nota' | 'attivita' | 'conversazione' | 'email' | 'marketing' | 'flusia';

/**
 * Niente pastiglia colorata dietro l'icona: le tessere tinte in fila sono la firma
 * delle interfacce sfornate in serie e appesantiscono una cronologia lunga. L'icona
 * resta nuda, tinta solo il tratto, e l'allineamento e' garantito dalla misura
 * fissa. Il colore e' comunque leggibile: e' l'unica cosa colorata della riga.
 */
const ACCENTI: Record<Exclude<CardAccent, 'flusia'>, { testo: string }> = {
  nota: { testo: 'text-[#787774] dark:text-white/50' },
  attivita: { testo: 'text-[#9A6A0A] dark:text-[#E5A84B]' },
  conversazione: { testo: 'text-[#448361] dark:text-[#6BBF8E]' },
  email: { testo: 'text-[#1B6FC2] dark:text-[#6BB2F5]' },
  marketing: { testo: 'text-[#9065B0] dark:text-[#BC9BD6]' },
};

/**
 * Tessera 32px per la colonna immagine quando non c'e' una foto vera
 * (newsletter, email): sfondo della famiglia al 14%, glifo pieno. E' il
 * ripiego di un avatar, non un badge: una sola per card, grande quanto una foto.
 */
export function mediaTileCls(accent: CardAccent = 'flusia'): string {
  const base = 'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg';
  if (accent === 'flusia') return `${base} ${ICON_CHIP_CLS.replace(/flex-shrink-0 h-\[22px\] w-\[22px\] flex items-center justify-center /, '')} bg-[rgba(233,30,140,0.10)]`;
  const sfondo: Record<Exclude<CardAccent, 'flusia'>, string> = {
    nota: 'bg-[rgba(120,119,116,0.12)] dark:bg-white/10',
    attivita: 'bg-[rgba(217,164,65,0.16)] dark:bg-[rgba(217,164,65,0.22)]',
    conversazione: 'bg-[rgba(91,169,122,0.16)] dark:bg-[rgba(91,169,122,0.22)]',
    email: 'bg-[rgba(74,155,232,0.14)] dark:bg-[rgba(74,155,232,0.22)]',
    marketing: 'bg-[rgba(168,124,196,0.16)] dark:bg-[rgba(168,124,196,0.22)]',
  };
  return `${base} ${sfondo[accent]} ${ACCENTI[accent].testo}`;
}

/** Icona 22px senza sfondo, col solo tratto tinto della famiglia. */
export function iconChipCls(accent: CardAccent = 'flusia'): string {
  if (accent === 'flusia') return ICON_CHIP_CLS;
  return `flex-shrink-0 h-[22px] w-[22px] flex items-center justify-center ${ACCENTI[accent].testo}`;
}

/** Etichetta del tipo in maiuscoletto, col colore della famiglia. */
export function kickerCls(accent: CardAccent = 'flusia'): string {
  if (accent === 'flusia') return KICKER_CLS;
  return `text-[10px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap ${ACCENTI[accent].testo}`;
}

/** Etichetta del tipo in maiuscoletto col gradiente Flusia. */
export const KICKER_CLS =
  'text-[10px] font-semibold uppercase tracking-[0.06em] whitespace-nowrap ' +
  'bg-gradient-to-r from-[#E91E8C] to-[#F97316] bg-clip-text text-transparent';

/** Bottone dentro il cluster azioni (il colore hover lo aggiunge il chiamante). */
export const ACTION_BTN_CLS =
  'p-1 rounded-full text-[#9B9A97] dark:text-white/40 hover:bg-[rgba(55,53,47,0.08)] dark:hover:bg-white/10 transition-colors';

// Le chip non si spezzano e non si comprimono: in una testa `flex-wrap` vanno a capo intere.
const CHIP_BASE_CLS =
  'inline-flex items-center gap-1 px-2 py-[1px] rounded-full text-[10px] font-medium ' +
  'whitespace-nowrap flex-shrink-0';

/** Chip neutra (provenienza, solo lettura). */
export const CHIP_NEUTRAL_CLS =
  `${CHIP_BASE_CLS} bg-[rgba(55,53,47,0.06)] dark:bg-white/10 text-[#787774] dark:text-gray-300`;

/** Chip semantica verde (attività completata). */
export const CHIP_DONE_CLS =
  `${CHIP_BASE_CLS} bg-[rgba(15,123,108,0.12)] text-[#0F7B6C] dark:text-emerald-300`;

/** Chip semantica rossa (attività in ritardo). */
export const CHIP_LATE_CLS =
  `${CHIP_BASE_CLS} bg-[rgba(212,76,71,0.12)] text-[#D44C47] dark:text-red-300`;

/** Pill del cambio stato, inline accanto al kicker. */
export const STATE_PILL_CLS =
  'inline-flex items-center gap-1.5 px-2.5 py-[2px] rounded-full text-[11px] whitespace-nowrap flex-shrink-0 ' +
  'bg-[#F7F7F5] dark:bg-[#373C3F] text-[#787774] dark:text-gray-400';

/** Pallino di default per gli stati fuori palette (M19). */
export const STATE_DOT_FALLBACK = '#9B9A97';

/** Riga meta sotto il titolo (autore, assegnatario, scadenza). */
export const CARD_META_CLS =
  'flex flex-wrap items-center gap-x-3.5 gap-y-1 mt-1.5 text-[11px] text-[#787774] dark:text-gray-400';

/** Blocco esito: divisore sottile sopra la label maiuscoletta. */
export const OUTCOME_WRAP_CLS = 'mt-2.5 pt-2 border-t border-[rgba(55,53,47,0.09)] dark:border-[#3F4447]';
export const OUTCOME_LABEL_CLS = 'text-[10px] uppercase tracking-wider text-[#787774] dark:text-gray-400 font-semibold';

function toDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Data compatta per le date secondarie: "12 ago" nell'anno corrente, "12 ago 2025" altrove.
 */
export function formatCompactDate(value: string | Date | null | undefined, now: Date = new Date()): string {
  const date = toDate(value);
  if (!date) return '';
  const base = date.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' }).replace('.', '');
  return date.getFullYear() === now.getFullYear() ? base : `${base} ${date.getFullYear()}`;
}

/** Ora nel formato "14:32" (le card sotto una chip giorno mostrano solo l'ora). */
export function formatTime(value: string | Date | null | undefined): string {
  const date = toDate(value);
  if (!date) return '';
  return date.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
}
